import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    console.error('ErrorBoundary: getDerivedStateFromError', error);
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary: componentDidCatch', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  private clearAuthAndRedirect = () => {
    localStorage.clear();
    sessionStorage.clear();

    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleLogout = () => {
    this.clearAuthAndRedirect();
  };

  public render() {
    if (this.state.hasError) {
      // Use plain HTML/CSS to avoid any Material-UI rendering issues
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          padding: '24px',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            maxWidth: '600px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{ color: '#d32f2f', marginTop: 0 }}>
              Oops! Something went wrong
            </h1>

            <p>We encountered an unexpected error. This might be due to:</p>

            <ul>
              <li>An expired session</li>
              <li>A network issue</li>
              <li>An unexpected application error</li>
            </ul>

            <div style={{ marginTop: '24px' }}>
              <button
                onClick={this.handleReload}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  marginBottom: '12px'
                }}
              >
                Reload Page
              </button>

              <button
                onClick={this.handleLogout}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#d32f2f',
                  border: '2px solid #d32f2f',
                  borderRadius: '4px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Log Out and Return to Login
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div style={{ marginTop: '24px' }}>
                <h3>Error Details (Development Only):</h3>
                <pre style={{
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  overflow: 'auto',
                  maxHeight: '200px',
                  fontSize: '12px',
                  borderRadius: '4px'
                }}>
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
